/**
 * Construction-specific types for construction company features
 */

import { CostCenter } from './cost-center';

/**
 * Construction Work - extends CostCenter with calculated financial metrics
 */
export interface ConstructionWork extends CostCenter {
  actualCost: number;      // Calculated: sum of paid expenses
  revenue: number;         // Calculated: sum of paid income
  profitMargin: number;    // Calculated: (revenue - actualCost) / revenue * 100
  variance: number;        // budgeted_cost - actualCost
  variancePercentage: number; // (variance / budgeted_cost) * 100
  status: WorkStatus;
}

export enum WorkStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  OVER_BUDGET = 'over_budget',
  UNDER_BUDGET = 'under_budget',
  ON_BUDGET = 'on_budget',
  NO_BUDGET = 'no_budget',
}

/**
 * Milestone Payment - stage-based payment for construction works
 */
export interface MilestonePayment {
  id: string;
  workId: string | number;
  workName: string;
  stageName: string;        // e.g., "Sinal", "Entrega Estrutura", "Conclusão"
  percentage: number;       // 0-100
  amount: number;
  dueDate: string;          // ISO date string
  isPaid: boolean;
  paidDate?: string;        // ISO date string
  transactionId?: string;   // Link to transaction in database
}

/**
 * Construction Dashboard Metrics
 */
export interface ConstructionMetrics {
  worksInProgress: number;
  averageProfitability: number;
  completedThisMonth: number;
  worksOverBudget: ConstructionWork[];
  totalRevenue: number;
  totalCosts: number;
  totalProfit: number;
  profitMarginAvg: number;
}

/**
 * Budget vs Actual Row
 */
export interface BudgetVsActualRow {
  workId: string | number;
  workName: string;
  workCode?: number;
  budgetedCost: number | null;
  actualCost: number;
  variance: number;
  variancePercentage: number;
  status: BudgetStatus;
}

export enum BudgetStatus {
  UNDER_BUDGET = 'under_budget',    // variance > 5%
  ON_BUDGET = 'on_budget',          // variance between -5% and +5%
  OVER_BUDGET = 'over_budget',      // variance < -5%
  NO_BUDGET = 'no_budget',          // budgeted_cost is null
  INVALID_BUDGET = 'invalid_budget', // budgeted_cost is 0
}

/**
 * Milestone Schedule for a work
 */
export interface MilestoneSchedule {
  workId: string | number;
  workName: string;
  totalValue: number;
  milestones: MilestonePayment[];
  paidCount: number;
  totalCount: number;
  nextDueDate?: string;
}

/**
 * Quick Category for expense entry
 */
export interface QuickCategory {
  name: string;
  icon: React.ComponentType<{ className?: string }>; // Lucide icon component
  type: 'expense' | 'income';
  color?: string;
}

// Status colors for UI
export const WORK_STATUS_COLORS: Record<WorkStatus, string> = {
  [WorkStatus.IN_PROGRESS]: 'text-blue-600 bg-blue-50 border-blue-200',
  [WorkStatus.COMPLETED]: 'text-green-600 bg-green-50 border-green-200',
  [WorkStatus.OVER_BUDGET]: 'text-red-600 bg-red-50 border-red-200',
  [WorkStatus.UNDER_BUDGET]: 'text-green-600 bg-green-50 border-green-200',
  [WorkStatus.ON_BUDGET]: 'text-yellow-600 bg-yellow-50 border-yellow-200',
  [WorkStatus.NO_BUDGET]: 'text-gray-600 bg-gray-50 border-gray-200',
};

export const BUDGET_STATUS_COLORS: Record<BudgetStatus, string> = {
  [BudgetStatus.UNDER_BUDGET]: 'bg-green-50 text-green-700 border-green-200',
  [BudgetStatus.ON_BUDGET]: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  [BudgetStatus.OVER_BUDGET]: 'bg-red-50 text-red-700 border-red-200',
  [BudgetStatus.NO_BUDGET]: 'bg-gray-50 text-gray-600 border-gray-200',
  [BudgetStatus.INVALID_BUDGET]: 'bg-orange-50 text-orange-700 border-orange-200',
};

export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  [BudgetStatus.UNDER_BUDGET]: 'Abaixo do orçamento',
  [BudgetStatus.ON_BUDGET]: 'No orçamento',
  [BudgetStatus.OVER_BUDGET]: 'Acima do orçamento',
  [BudgetStatus.NO_BUDGET]: 'Sem orçamento',
  [BudgetStatus.INVALID_BUDGET]: 'Orçamento inválido',
};

// Utility functions
export const calculateVariance = (budgeted: number | null, actual: number): number => {
  if (budgeted === null || budgeted === undefined) return 0;
  return budgeted - actual;
};

export const calculateVariancePercentage = (budgeted: number | null, actual: number): number => {
  if (budgeted === null || budgeted === undefined || budgeted === 0) return 0;
  return ((budgeted - actual) / budgeted) * 100;
};

export const determineBudgetStatus = (variance: number, budgeted: number | null): BudgetStatus => {
  if (budgeted === null || budgeted === undefined) return BudgetStatus.NO_BUDGET;
  if (budgeted === 0) return BudgetStatus.INVALID_BUDGET;

  const variancePercentage = (variance / budgeted) * 100;

  if (variancePercentage > 5) return BudgetStatus.UNDER_BUDGET;
  if (variancePercentage < -5) return BudgetStatus.OVER_BUDGET;
  return BudgetStatus.ON_BUDGET;
};

export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
};

export const formatVariance = (variance: number): string => {
  const prefix = variance >= 0 ? '+' : '';
  return `${prefix}${formatCurrency(variance)}`;
};
